import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  // ⚠️ สลับระหว่าง localhost และ production
  // 🔹 สำหรับ localhost: uncomment บรรทัดที่ 1, comment บรรทัดที่ 2
  // 🔹 สำหรับ production: comment บรรทัดที่ 1, uncomment บรรทัดที่ 2
 // const API_BASE_URL = 'http://localhost:8000';                                    // [1] localhost
  const API_BASE_URL = 'https://mfuwellnesscenter-server.dietz.asia';           // [2] production

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('mfu_token');
      const storedUser = localStorage.getItem('mfu_user');

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);

          // ✅ ตั้งค่า user และ token จาก localStorage ก่อนเพื่อให้แสดงหน้าได้ทันที
          setToken(storedToken);
          setUser(parsedUser);
          setLoading(false); // ⭐ ตั้ง loading เป็น false ทันทีเพื่อให้หน้าแสดงได้

          // เริ่ม verify token กับ backend แบบ background
          setIsVerifying(true);

          try {
            const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
              token: storedToken
            }, {
              timeout: 10000 // เพิ่ม timeout เป็น 10 วินาที
            });

            if (response.data.status === 'success' && response.data.data) {
              const userData = response.data.data.user;

              // ตรวจสอบว่า user มี role 'admin' หรือไม่
              const userRoles = Array.isArray(userData.role) ? userData.role : [userData.role];
              const hasAdminRole = userRoles.includes('admin');

              if (hasAdminRole) {
                // Token is valid และมี role admin - อัปเดต user ด้วยข้อมูลล่าสุด
                setUser(userData);
                // อัปเดต localStorage ด้วยข้อมูลล่าสุด (รวม role)
                localStorage.setItem('mfu_user', JSON.stringify(userData));
                console.log('✅ Token verified (Admin), user role:', userData.role);
              } else {
                // ไม่มี role admin, clear localStorage
                console.warn('⚠️ User does not have admin role:', userRoles);
                localStorage.removeItem('mfu_token');
                localStorage.removeItem('mfu_user');
                setToken(null);
                setUser(null);
              }
            } else {
              // Token is invalid (backend ตอบกลับว่าไม่ valid), clear localStorage
              console.warn('⚠️ Token invalid from backend response');
              localStorage.removeItem('mfu_token');
              localStorage.removeItem('mfu_user');
              setToken(null);
              setUser(null);
            }
          } catch (verifyError) {
            // แยกประเภทของ error
            if (verifyError.code === 'ECONNABORTED' || verifyError.message?.includes('timeout')) {
              // Network timeout - ไม่ล้าง localStorage, ให้ใช้ข้อมูลเดิม
              console.warn('⚠️ Token verification timeout, using cached user data');
            } else if (verifyError.response?.status === 401 || verifyError.response?.status === 403) {
              // Backend ตอบกลับว่า token ไม่ valid - ล้าง localStorage
              console.error('❌ Token unauthorized/forbidden, clearing localStorage');
              localStorage.removeItem('mfu_token');
              localStorage.removeItem('mfu_user');
              setToken(null);
              setUser(null);
            } else {
              // Network error อื่นๆ - ไม่ล้าง localStorage, ให้ใช้ข้อมูลเดิม
              console.warn('⚠️ Token verification network error, using cached user data:', verifyError.message);
            }
          } finally {
            setIsVerifying(false);
          }
        } catch (parseError) {
          // ถ้า parse localStorage ผิดพลาด ให้ล้างข้อมูล
          console.error('❌ Error parsing stored user data:', parseError);
          localStorage.removeItem('mfu_token');
          localStorage.removeItem('mfu_user');
          setLoading(false);
        }
      } else {
        // ไม่มี token หรือ user ใน localStorage
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login with username and password
  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      });

      if (response.data.status === 'success') {
        const { user: userData, token: tokenData } = response.data.data;

        // Save to state
        setUser(userData);
        setToken(tokenData);

        // Save to localStorage
        localStorage.setItem('mfu_token', tokenData);
        localStorage.setItem('mfu_user', JSON.stringify(userData));

        console.log('✅ Login success (Admin), user role:', userData.role);
        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
      };
    }
  };

  // Login with token (auto-login from URL)
  const loginWithToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString
      });

      if (response.data.status === 'success' && response.data.data) {
        const userData = response.data.data.user;

        // Save to state
        setUser(userData);
        setToken(tokenString);

        // Save to localStorage
        localStorage.setItem('mfu_token', tokenString);
        localStorage.setItem('mfu_user', JSON.stringify(userData));

        console.log('✅ Login with token success (Admin), user role:', userData.role);
        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message || 'Invalid token' };
      }
    } catch (error) {
      console.error('Token login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Token ไม่ถูกต้องหรือหมดอายุ'
      };
    }
  };

  // Verify token validity
  const verifyToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString || token
      });

      if (response.data.status !== 'success') {
        throw new Error('Token is invalid');
      }

      return true;
    } catch (error) {
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      if (user?.id) {
        await axios.post(`${API_BASE_URL}/api/auth/logout`, {
          user_id: user.id
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state
      setUser(null);
      setToken(null);

      // Clear localStorage
      localStorage.removeItem('mfu_token');
      localStorage.removeItem('mfu_user');
    }
  };

  // Get user profile
  const getProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.status === 'success') {
        const userData = response.data.data.user;
        setUser(userData);
        localStorage.setItem('mfu_user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Get profile error:', error);
      return null;
    }
  };

  // Get current user's full name
  const getUserFullName = () => {
    if (!user) return 'ไม่ระบุ';

    // ถ้ามี fullName อยู่แล้ว ใช้เลย
    if (user.fullName) return user.fullName;

    // ไม่อย่างนั้นให้รวม nameprefix + name + lastname
    const parts = [];
    if (user.nameprefix) parts.push(user.nameprefix);
    if (user.name) parts.push(user.name);
    if (user.lastname) parts.push(user.lastname);

    return parts.length > 0 ? parts.join(' ') : (user.username || 'ไม่ระบุ');
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    loginWithToken,
    logout,
    getProfile,
    getUserFullName
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
